import type { AppType } from '@headline/api'
import { hc } from 'hono/client'
import { redirectOnUnauthorized } from './session-expiry'

/**
 * End-to-end typed client. `AppType` is a type-only import from apps/api, so nothing server-side
 * is bundled — but every path, query param, and response shape is inferred. Change a route's
 * contract and this file's callers fail typecheck in CI.
 *
 * Relative base URL: Vite proxies /api in dev, Hono serves it directly in prod.
 *
 * **No auth code here, deliberately.** The AuthKit session is an HttpOnly cookie the browser
 * attaches on its own, so there is nothing for JS to read, refresh, or forget to send — which is
 * the point of keeping auth server-side. `credentials: 'same-origin'` is the fetch default, and
 * naming it is a reminder that this only works because the SPA and the API share an origin
 * (Vite's proxy in dev, one container in prod). A cross-origin API would need CORS with
 * credentials, and the cookie's SameSite=Lax would then have to be revisited.
 */
export const api = hc<AppType>('/', {
  fetch: async (input: RequestInfo | URL, init?: RequestInit) => {
    const res = await fetch(input, { ...init, credentials: 'same-origin' })
    // A session that ended while the tab was open: go and sign in rather than keep polling into
    // 401s (lib/session-expiry.ts).
    redirectOnUnauthorized(res.status, input instanceof Request ? input.url : String(input))
    return res
  },
})
